/*----------------------------------
- ROUTE: /refund-policy
----------------------------------*/

/**
 * @docs docs/features/website-legal-pages
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { definePageRoute } from '@common/router/definitions';

import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { REFUND_POLICY_PAGE_CONTENT } from '@/client/catalogs/landing/legal/refundPolicy/pageContent';
import {
    PUBLIC_INDEX_ROBOTS_DIRECTIVES,
    UNPUBLISHED_PAGE_ROBOTS_DIRECTIVES,
} from '@/common/catalogs/crawlerPolicy';
import { isLegalEntityResolved } from '@/common/catalogs/legal/entity';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import {
    buildLegalPageStructuredData,
    type WebsiteJsonLdNode,
} from '@/client/pages/landing/seo/structuredData';
import LegalPage from '@/client/pages/landing/legal/components/LegalPage';
import { cachedLandingHeaderNavigation } from '@/common/libs/connected/publicProductReadCache';

/*----------------------------------
- ROUTES
----------------------------------*/

export default definePageRoute({
    path: '/refund-policy',
    options: {
        auth: false,
        layout: false,
        static: {
            refresh: '1 day',
            urls: ['/refund-policy'],
        },
    },
    data: ({ Product }) => ({
        navigation: cachedLandingHeaderNavigation.read() ?? Product.Navigation.GetLandingHeaderNavigation(),
    }),
    render: ({
        page,
        Router,
        navigation,
    }: {
        page: {
            description?: string;
            head: Array<{ $: string; [key: string]: string | Date | null | undefined }>;
            jsonld: WebsiteJsonLdNode[];
            title?: string;
            url: string;
        };
        Router: { url: (path: string) => string };
        navigation: LandingHeaderDomainsNavigation;
    }) => {
        const content = REFUND_POLICY_PAGE_CONTENT;
        const canonicalUrl = Router.url('/refund-policy');

        page.title = content.meta.pageTitle;
        page.description = content.meta.metaDescription;
        page.url = canonicalUrl;
        // Trader identification is mandatory before this page can be indexed.
        page.head.push({
            $: 'meta',
            name: 'robots',
            content: isLegalEntityResolved()
                ? PUBLIC_INDEX_ROBOTS_DIRECTIVES
                : UNPUBLISHED_PAGE_ROBOTS_DIRECTIVES,
        });
        page.jsonld.push(
            ...buildLegalPageStructuredData({
                canonicalUrl,
                description: content.meta.metaDescription,
                title: content.meta.pageTitle,
            }),
        );

        return (
            <LegalPage
                content={content}
                currentPath={LANDING_WEBSITE_PAGE_ROUTES.refundPolicy}
                trackingPage="refund-policy"
                navigation={navigation}
            />
        );
    },
});
